// client/src/pages/DownloadStatsAdmin.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/DownloadStatsAdmin.css';

// Icônes SVG inline
const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const IconUser = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const DownloadStatsAdmin = () => {
  const [stats, setStats] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchEmail, setSearchEmail] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback
  const defaultImage = '/images/gallery-default.png';
  
  // Fonction pour charger les statistiques globales
  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/download-stats');
      setStats(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      setError('Erreur lors du chargement des statistiques');
      setLoading(false);
    }
  };
  
  // Fonction pour charger les détails des téléchargements
  const fetchDownloadDetails = async (page = 1) => {
    try {
      setDetailsLoading(true);
      
      // Construire l'URL avec les paramètres de filtrage
      let url = `/api/download-stats/details?page=${page}`;
      
      if (searchEmail) {
        url += `&email=${encodeURIComponent(searchEmail)}`;
      }
      
      if (dateRange.startDate && dateRange.endDate) {
        url += `&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      }
      
      const response = await axios.get(url);
      
      setDownloads(response.data.downloads);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setDetailsLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des détails:', err);
      setError('Erreur lors du chargement des détails de téléchargement');
      setDetailsLoading(false);
    }
  };
  
  // Charger les données au montage du composant
  useEffect(() => {
    fetchStats();
    fetchDownloadDetails();
  }, []);
  
  // Recharger les détails lors du changement de page ou des filtres
  useEffect(() => {
    fetchDownloadDetails(currentPage);
  }, [currentPage]);
  
  // Fonction pour gérer la recherche
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDownloadDetails(1);
  };
  
  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setSearchEmail('');
    setDateRange({
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
    fetchDownloadDetails(1);
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (imageId) => {
    console.log(`Erreur de chargement d'image ${imageId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [imageId]: true
    }));
  };
  
  // Obtenir l'URL de l'image thumbnail
  const getThumbnailUrl = (filename, imageId) => {
    if (!filename || imageErrors[imageId]) return defaultImage;
    
    // Utiliser la route API avec un timestamp pour éviter le cache
    const timestamp = new Date().getTime();
    return `/api/gallery/thumbnail/${filename}?t=${timestamp}`;
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  // Navigation - Page précédente
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Navigation - Page suivante
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Exporter les données au format CSV
  const exportToCsv = () => {
    // Construire l'en-tête CSV
    const headers = ['Email', 'Image', 'Galerie', 'Téléchargé le', 'Nombre de téléchargements', 'IP', 'User Agent'];
    
    // Convertir les téléchargements en lignes CSV
    const csvRows = downloads.map(download => {
      const image = download.imageId ? download.imageId.titre : 'Image supprimée';
      const gallery = download.imageId && download.imageId.galleryId ? download.imageId.galleryId.titre : 'Galerie inconnue';
      
      return [
        download.email,
        image,
        gallery,
        formatDate(download.lastDownload),
        download.downloadCount,
        download.ipAddress || '-',
        download.userAgent || '-'
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });
    
    // Assembler le CSV complet
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    // Créer un Blob et télécharger
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `telechargements_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (loading) return <div className="loading">Chargement des statistiques...</div>;
  if (error && !stats) return <div className="error-message">{error}</div>;
  
  return (
    <div className="download-stats-admin-page">
      <div className="download-stats-header">
        <h1>Statistiques de Téléchargement</h1>
        <Link to="/admin/gallery" className="back-to-gallery">
          &laquo; Retour à la gestion des galeries
        </Link>
      </div>
      
      {error && <div className="message error">{error}</div>}
      
      {stats && (
        <div className="stats-overview">
          <div className="stats-card">
            <div className="stats-icon">
              <IconDownload />
            </div>
            <div className="stats-info">
              <h3>{stats.totalDownloadCount}</h3>
              <p>Téléchargements totaux</p>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="stats-icon">
              <IconUser />
            </div>
            <div className="stats-info">
              <h3>{stats.totalUniqueEmails}</h3>
              <p>Emails uniques</p>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="stats-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
                <path d="M8 7V3m8 4V3"></path>
                <path d="M3 7h18"></path>
              </svg>
            </div>
            <div className="stats-info">
              <h3>{stats.totalDownloads}</h3>
              <p>Liens générés</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="stats-details">
        <div className="stats-section">
          <h2>Top 5 des Images Téléchargées</h2>
          
          {stats && stats.topImages && (
            <div className="top-images-grid">
              {stats.topImages.map((image, index) => (
                <div key={image._id} className="top-image-card">
                  <div className="rank-badge">{index + 1}</div>
                  <div className="top-image-thumbnail">
                    <img 
                      src={getThumbnailUrl(image.miniature, image._id)} 
                      alt={image.titre}
                      onError={() => handleImageError(image._id)} 
                    />
                  </div>
                  <div className="top-image-info">
                    <h4>{image.titre}</h4>
                    <p>{image.galleryName}</p>
                    <div className="top-downloads">{image.downloads} téléchargements</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="stats-section">
          <h2>Téléchargements des 7 derniers jours</h2>
          
          {stats && stats.last7Days && (
            <div className="chart-container">
              <div className="bar-chart">
                {stats.last7Days.map(day => (
                  <div key={day.date} className="chart-bar-container">
                    <div 
                      className="chart-bar" 
                      style={{ 
                        height: `${(day.count / Math.max(...stats.last7Days.map(d => d.count))) * 100}%` 
                      }}
                    >
                      <span className="chart-value">{day.count}</span>
                    </div>
                    <div className="chart-label">{day.date.split('-').slice(1).join('/')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="download-details-section">
        <div className="section-header">
          <h2>Détails des Téléchargements</h2>
          <button className="export-button" onClick={exportToCsv}>
            Exporter en CSV
          </button>
        </div>
        
        <div className="filters-container">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-container">
              <IconSearch className="search-icon" />
              <input
                type="email"
                placeholder="Rechercher par email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
              />
            </div>
            
            <div className="date-range">
              <div className="date-input">
                <label>Du</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                />
              </div>
              <div className="date-input">
                <label>Au</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                />
              </div>
            </div>
            
            <div className="search-buttons">
              <button type="submit" className="search-button">
                Filtrer
              </button>
              <button type="button" className="reset-button" onClick={resetFilters}>
                Réinitialiser
              </button>
            </div>
          </form>
        </div>
        
        {detailsLoading ? (
          <div className="loading-details">Chargement des détails...</div>
        ) : downloads.length === 0 ? (
          <div className="no-results">
            <p>Aucun téléchargement trouvé</p>
          </div>
        ) : (
          <>
            <div className="downloads-table-container">
              <table className="downloads-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Image</th>
                    <th>Galerie</th>
                    <th>Téléchargé le</th>
                    <th>Nombre</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {downloads.map(download => (
                    <tr key={download._id}>
                      <td>{download.email}</td>
                      <td>
                        {download.imageId ? (
                          <div className="image-cell">
                            <img 
                              src={getThumbnailUrl(download.imageId.miniature, download.imageId._id)} 
                              alt={download.imageId.titre}
                              onError={() => handleImageError(download.imageId._id)}
                            />
                            <span>{download.imageId.titre}</span>
                          </div>
                        ) : (
                          <span className="deleted-image">Image supprimée</span>
                        )}
                      </td>
                      <td>
                        {download.imageId && download.imageId.galleryId ? (
                          download.imageId.galleryId.titre
                        ) : (
                          <span className="unknown-gallery">Galerie inconnue</span>
                        )}
                      </td>
                      <td>{formatDate(download.lastDownload)}</td>
                      <td>{download.downloadCount}</td>
                      <td>{download.ipAddress || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="pagination">
              <button 
                onClick={goToPreviousPage} 
                disabled={currentPage === 1}
                className="pagination-button"
              >
                &laquo; Précédent
              </button>
              <span className="pagination-info">
                Page {currentPage} sur {totalPages}
              </span>
              <button 
                onClick={goToNextPage} 
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Suivant &raquo;
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DownloadStatsAdmin;